import type { Dispatch } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildHeadersWithWidgetToken } from '@elfsight/lib-utils';
import { DATA_SERVICE_URL } from '../../../constants';
import type {
  Field,
  FieldType,
  FileFieldValue,
  FormAction,
  UploadedFileType
} from '../../contexts';
import { useFormDispatch } from '../../contexts';

export const ERROR_TYPE = 'error_upload';
const DELAY_BEFORE_SENDING_DATA = 1000;
const UPLOADABLE_FIELD_TYPES: FieldType[] = ['file', 'signature'];

const isUploadableFieldType = (type: FieldType) => {
  return UPLOADABLE_FIELD_TYPES.includes(type);
};

type FileProps = {
  id: string;
  file: UploadedFileType;
  abortController: AbortController;
  type?: FieldType;
};

type UploadProps = FileProps & {
  widgetToken: string | undefined;
};

type UploadedFilesProps = {
  fields: { [index: string]: Field };
  files: FileProps[];
  dispatch: Dispatch<FormAction>;
  widgetToken: string | undefined;
};

const upload = async ({
  id,
  file,
  abortController,
  widgetToken
}: UploadProps) => {
  const formData = new FormData();
  formData.append(`files[${id}][]`, file, file.name);

  const headers = buildHeadersWithWidgetToken(widgetToken);

  try {
    const response = await fetch(
      `${DATA_SERVICE_URL}/public/resources/attachments`,
      {
        method: 'POST',
        headers,
        body: formData,
        signal: abortController.signal
      }
    );

    const url = await response.json();
    return url[id][0] ?? ERROR_TYPE;
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    console.error(error);
    return ERROR_TYPE;
  } finally {
    formData?.delete(`files[${id}][]`);
  }
};

const uploadedFiles = async ({
  fields,
  files,
  dispatch,
  widgetToken
}: UploadedFilesProps) => {
  const requests = files.map(async ({ id, file, abortController, type }) => {
    try {
      const url = await upload({ id, file, abortController, widgetToken });
      if (url === undefined || abortController.signal.aborted) return;
      Object.defineProperty(file, 'uploaded', {
        value: url,
        enumerable: true
      });
      const prevFiles = (fields[id].value as FileFieldValue)?.filter(
        ({ name }) => name !== file.name
      );

      const fieldType = type || fields[id].type || 'file';

      dispatch({
        type: 'changeField',
        field: { ...fields[id], value: [...prevFiles, file], type: fieldType }
      });
    } catch (error) {
      console.error('Error in uploadedFiles:', error);
    }
  });

  await Promise.allSettled(requests);
  return;
};

export const useUploadFiles = (
  fields: { [index: string]: Field },
  widgetToken: string | undefined
) => {
  const fileList = useRef<FileProps[]>([]);
  const [uploadStatus, setUploadStatus] = useState(false);
  const changedFields = Object.values(fields);
  const dispatch = useFormDispatch();

  const createFileProps = useCallback(
    (
      fieldId: string,
      fieldType: FieldType,
      file: UploadedFileType
    ): FileProps => {
      const prevFile = fileList.current.find(
        ({ file: currFile, id: currId }) =>
          currFile.name === file.name && currId === fieldId
      );
      const abortController =
        prevFile?.abortController || new AbortController();

      return {
        id: fieldId,
        file,
        abortController,
        type: fieldType
      };
    },
    []
  );

  const getUploadableFields = useCallback(
    (targetType?: FieldType) => {
      return changedFields.filter(({ type }) => {
        if (targetType) {
          return type === targetType;
        }

        return isUploadableFieldType(type);
      });
    },
    [changedFields]
  );

  const fieldsToFileProps = useCallback(
    (targetFields: Field[]): FileProps[] => {
      return targetFields.flatMap(({ id, type, value }) => {
        const fieldValue = value as FileFieldValue;

        if (!fieldValue?.length) {
          return [];
        }

        return fieldValue.map((file) => createFileProps(id, type, file));
      });
    },
    [createFileProps]
  );

  const filterUnuploadedFiles = useCallback(
    (fileProps: FileProps[]): FileProps[] => {
      return fileProps.filter(({ file }) => !file.uploaded);
    },
    []
  );

  const executeUpload = useCallback(
    async (fileProps: FileProps[]) => {
      const filesToUpload = filterUnuploadedFiles(fileProps);
      if (!filesToUpload.length) {
        return [];
      }

      setUploadStatus(true);
      try {
        await uploadedFiles({
          fields,
          files: filesToUpload,
          dispatch,
          widgetToken
        });
        return filesToUpload.map(({ id, file }) => ({ fieldId: id, file }));
      } finally {
        setUploadStatus(false);
      }
    },
    [fields, dispatch, widgetToken, filterUnuploadedFiles]
  );

  const currentFileList = useMemo(() => {
    const fileFields = getUploadableFields('file');

    return fieldsToFileProps(fileFields);
  }, [getUploadableFields, fieldsToFileProps]);

  useEffect(() => {
    const isAddedFiles =
      !fileList.current.length ||
      currentFileList.length > fileList.current.length;

    if (isAddedFiles) {
      fileList.current = currentFileList;
      return;
    }

    const currentFileKeys = currentFileList.map(
      ({ file, id }) => `${id}_${file.name}`
    );
    const deletedFile = fileList.current.find(
      ({ file, id }) => !currentFileKeys.includes(`${id}_${file.name}`)
    );

    if (deletedFile) {
      deletedFile.abortController.abort();
      fileList.current = currentFileList;
    }
  }, [currentFileList]);

  useEffect(() => {
    return () => {
      fileList.current.forEach((f) => f.abortController.abort());
      fileList.current = [];
    };
  }, []);

  const startFileWatcher = useCallback(() => {
    if (uploadStatus) {
      return;
    }

    const debounce = setTimeout(
      () => executeUpload(currentFileList),
      DELAY_BEFORE_SENDING_DATA
    );

    return () => clearTimeout(debounce);
  }, [currentFileList, uploadStatus, executeUpload]);

  const uploadFiles = useCallback(
    async (fieldIds: string | string[]) => {
      const targetFieldIds = Array.isArray(fieldIds) ? fieldIds : [fieldIds];

      const targetFields = Object.entries(fields)
        .filter(([id, field]) => {
          const shouldUpload = targetFieldIds.includes(id);
          const hasFiles = (field.value as FileFieldValue)?.length > 0;
          const isUploadableType = isUploadableFieldType(field.type);

          if (shouldUpload && !isUploadableType) {
            console.warn(
              `Field ${id} has type '${field.type}' which is not uploadable. Allowed types: '${UPLOADABLE_FIELD_TYPES.join(', ')}'`
            );

            return false;
          }

          return shouldUpload && hasFiles && isUploadableType;
        })
        .map(([_, field]) => field);

      if (!targetFields.length) {
        return;
      }

      const filePropsToUpload = fieldsToFileProps(targetFields);
      return await executeUpload(filePropsToUpload);
    },
    [fields, fieldsToFileProps, executeUpload]
  );

  return {
    startFileWatcher,
    uploadFiles
  };
};
